// console.log('🚀 ~ sib-main-collection-filters.js');

class SibMobileFilterDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = this.querySelector('.sib-mobile-filter-drawer');
    this.openButton = this.querySelector('.sib-collection-filters__mobile-header-inner');
    this.closeButton = this.querySelector('.sib-mobile-filter-drawer__close');
    this.overlay = this.querySelector('.sib-mobile-filter-drawer__overlay');
    this.mobileForm = this.querySelector('#CollectionFiltersMobileForm');
    this.mobileSortBy = this.querySelector('#SortByMobile');
    this.drawerTriggers = this.querySelectorAll('[data-drawer-trigger]');
    this.drawerContents = this.querySelectorAll('[data-drawer-content]');
    this.backButtons = this.querySelectorAll('[data-drawer-close]');
    this.showMoreButtons = this.querySelectorAll('[data-show-more]');
    this.mainApplyButton = this.querySelector('[data-main-drawer-apply]');
    this.mainResetButton = this.querySelector('[data-main-drawer-reset]');
    this.footerButtons = this.querySelector('.sib-mobile-filter-drawer__footer-buttons');
    this.activeNestedDrawer = null;
    this.filters = null;

    this.updateFooterButtonsVisibility();
    this.initializeFilters();
  }

  async initializeFilters() {
    // Wait for custom elements to be defined
    await customElements.whenDefined('sib-collection-filters');

    // Initialize filters after a small delay to ensure the component is mounted
    setTimeout(() => {
      this.filters = document.querySelector('sib-collection-filters');
      if (!this.filters) {
        // console.warn('Creating new SibCollectionFilters instance');
        this.filters = new SibCollectionFilters();
      }
      this.bindEvents();
      this.initializeShowMoreButtons();
    }, 100);
  }

  initializeShowMoreButtons() {
    this.showMoreButtons.forEach((button) => {
      const list = button.previousElementSibling;
      const items = list.querySelectorAll('li');
      if (items.length > 10) {
        const remainingCount = items.length - 10;
        button.textContent = `+ Show ${remainingCount} more`;
        items.forEach((item, index) => {
          if (index >= 10) {
            item.classList.add('is-hidden');
          }
        });
      } else {
        button.style.display = 'none';
      }
    });
  }

  bindEvents() {
    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));

    this.openButton?.addEventListener('click', this.openDrawer.bind(this));
    this.closeButton?.addEventListener('click', this.closeDrawer.bind(this));
    this.overlay?.addEventListener('click', this.closeDrawer.bind(this));

    // Main drawer buttons
    this.mainApplyButton?.addEventListener('click', this.applyMainFilters.bind(this));
    this.mainResetButton?.addEventListener('click', this.resetMainFilters.bind(this));

    // Form change events for both main and nested drawers
    this.mobileForm?.addEventListener('change', (event) => {
      const changedInput = event.target;
      const group = changedInput.closest('.sib-mobile-filter-drawer__nested');

      // Update footer buttons visibility
      this.updateFooterButtonsVisibility(group);

      // Sync with desktop form but don't trigger change event
      const desktopForm = document.querySelector('#CollectionFiltersForm');
      if (desktopForm) {
        const desktopInput = desktopForm.querySelector(
          `input[name="${changedInput.name}"][value="${changedInput.value}"]`
        );
        if (desktopInput) {
          if (changedInput.type === 'checkbox') {
            desktopInput.checked = changedInput.checked;
          } else if (changedInput.type === 'number') {
            desktopInput.value = changedInput.value;
          }
        }
      }

      // Don't auto-apply filters, wait for Apply button
    });

    // Handle mobile drawer filter remove buttons
    document.addEventListener('click', (event) => {
      const removeButton = event.target.closest('.sib-mobile-filter-drawer__remove-button');
      if (!removeButton) return;

      event.preventDefault();

      // Handle clear all
      if (removeButton.classList.contains('sib-mobile-filter-drawer__remove-all')) {
        this.resetMainFilters();
        return;
      }

      // Handle individual filter removal
      const filterKey = removeButton.dataset.filterKey;
      const filterValue = removeButton.dataset.filterValue;

      if (filterKey && filterValue) {
        // Uncheck desktop input
        const desktopForm = document.querySelector('#CollectionFiltersForm');
        if (desktopForm) {
          const desktopInput = desktopForm.querySelector(`input[name="${filterKey}"][value="${filterValue}"]`);
          if (desktopInput) {
            desktopInput.checked = false;
          }
        }

        // Uncheck mobile input
        if (this.mobileForm) {
          const mobileInput = this.mobileForm.querySelector(`input[name="${filterKey}"][value="${filterValue}"]`);
          if (mobileInput) {
            mobileInput.checked = false;
          }
        }

        // Update footer buttons visibility
        this.updateFooterButtonsVisibility();
      }
    });

    // Sync mobile sort with desktop
    this.mobileSortBy?.addEventListener('change', (event) => {
      const desktopSort = document.querySelector('#SortBy');
      if (desktopSort) {
        desktopSort.value = event.target.value;
        desktopSort.dispatchEvent(new Event('change'));
      }
      // Close drawer after sort selection
      this.closeDrawer();
    });

    // Nested drawer events
    this.drawerTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openNestedDrawer(event);
      });
    });

    this.backButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.closeNestedDrawer();
      });
    });

    // Show more buttons
    this.showMoreButtons.forEach((button) => {
      button.addEventListener('click', this.handleShowMore.bind(this));
    });

    // Nested drawer apply/reset buttons
    this.drawerContents.forEach((content) => {
      const applyButton = content.querySelector('[data-drawer-apply]');
      const resetButton = content.querySelector('[data-drawer-reset]');

      applyButton?.addEventListener('click', async () => {
        // Wait for filters to be initialized if not already
        if (!this.filters) {
          await this.initializeFilters();
        }

        if (!this.filters) {
          // console.warn('Filters component still not found');
          return;
        }

        const formData = new FormData(this.mobileForm);
        const searchParams = new URLSearchParams(formData);

        if (this.mobileSortBy?.value) {
          searchParams.set('sort_by', this.mobileSortBy.value);
        }

        // Close both drawers
        this.closeNestedDrawer();
        this.closeDrawer();

        // Apply filters via AJAX and update parent drawer state
        await this.filters.renderPage(searchParams.toString());
        this.updateFooterButtonsVisibility();
      });

      resetButton?.addEventListener('click', () => {
        this.resetNestedDrawer(content);
      });
    });
  }

  updateFooterButtonsVisibility(group) {
    const form = this.mobileForm;
    const buttons = group ? group.querySelector('.sib-mobile-filter-drawer__nested-buttons') : this.footerButtons;

    if (!form || !buttons) return;

    // Get all inputs within the specified group or form
    const inputs = group ? group.querySelectorAll('input') : form.querySelectorAll('input');
    const hasActiveFilters = Array.from(inputs).some((input) => {
      if (input.type === 'checkbox') {
        return input.checked;
      }
      if (input.type === 'number') {
        const value = input.value.trim();
        return value !== '' && value !== '0';
      }
      return false;
    });

    if (hasActiveFilters) {
      buttons.classList.remove('is-hidden');
    } else {
      buttons.classList.add('is-hidden');
    }
  }

  handleShowMore(event) {
    const button = event.currentTarget;
    const list = button.previousElementSibling;
    const items = list.querySelectorAll('li');
    const remainingCount = items.length - 10;

    if (button.classList.contains('is-expanded')) {
      // Show less
      items.forEach((item, index) => {
        if (index >= 10) {
          item.classList.add('is-hidden');
        }
      });
      button.textContent = `+ Show ${remainingCount} more`;
      button.classList.remove('is-expanded');
    } else {
      // Show more
      items.forEach((item) => item.classList.remove('is-hidden'));
      button.textContent = '- Show less';
      button.classList.add('is-expanded');
    }
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    if (this.activeNestedDrawer) {
      this.closeNestedDrawer();
    } else {
      this.closeDrawer();
    }
  }

  onFocusOut() {
    setTimeout(() => {
      // Don't close if we have an active nested drawer
      if (this.activeNestedDrawer) return;

      // Don't close if focus is still within the drawer or its nested content
      const focusedElement = document.activeElement;
      const isInDrawer = this.drawer.contains(focusedElement);
      const isInNestedDrawer = focusedElement?.closest('.sib-mobile-filter-drawer__nested');

      if (!isInDrawer && !isInNestedDrawer) {
        this.closeDrawer();
      }
    });
  }

  openNestedDrawer(event) {
    const trigger = event.currentTarget;
    const content = trigger.nextElementSibling;

    if (!content || !content.classList.contains('sib-mobile-filter-drawer__nested')) return;

    // Close any open nested drawer first
    if (this.activeNestedDrawer) {
      this.activeNestedDrawer.classList.remove('is-open');
    }

    content.classList.add('is-open');
    this.activeNestedDrawer = content;

    // Update focus management
    const firstFocusable = content.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  resetNestedDrawer(content) {
    if (!content || !this.filters) return;

    // Reset all checkboxes in this nested drawer
    const checkboxes = content.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    // Reset all number inputs in this nested drawer
    const numberInputs = content.querySelectorAll('input[type="number"]');
    numberInputs.forEach((input) => {
      input.value = '';
    });

    // Hide the nested drawer footer buttons
    const nestedFooter = content.querySelector('.sib-mobile-filter-drawer__nested-buttons');
    if (nestedFooter) {
      nestedFooter.classList.add('is-hidden');
    }

    // Apply the reset via AJAX
    const formData = new FormData(this.mobileForm);
    const searchParams = new URLSearchParams(formData);

    // Preserve sort if exists
    if (this.mobileSortBy?.value) {
      searchParams.set('sort_by', this.mobileSortBy.value);
    }

    // Close the nested drawer
    this.closeNestedDrawer();

    // Update the page
    this.filters.renderPage(searchParams.toString());
  }

  closeNestedDrawer() {
    if (!this.activeNestedDrawer) return;

    this.activeNestedDrawer.classList.remove('is-open');

    // Return focus to the trigger button
    const trigger = this.activeNestedDrawer.previousElementSibling;
    if (trigger) {
      trigger.focus();
    }

    this.activeNestedDrawer = null;
  }

  openDrawer() {
    this.drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    const focusElement = this.closeButton || this.drawer;
    trapFocus(this.drawer, focusElement);
  }

  closeDrawer() {
    if (this.activeNestedDrawer) {
      this.closeNestedDrawer();
    }
    this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    removeTrapFocus(this.openButton);
  }

  applyMainFilters() {
    if (!this.mobileForm || !this.filters) return;

    const formData = new FormData(this.mobileForm);
    const searchParams = new URLSearchParams(formData);

    if (this.mobileSortBy?.value) {
      searchParams.set('sort_by', this.mobileSortBy.value);
    }

    this.filters.renderPage(searchParams.toString());
    this.closeDrawer();
  }

  resetMainFilters() {
    if (!this.mobileForm || !this.filters) return;

    // Reset all form elements including nested drawers
    this.mobileForm.reset();

    // Reset all nested drawers
    this.drawerContents.forEach((content) => {
      const checkboxes = content.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });

      const numberInputs = content.querySelectorAll('input[type="number"]');
      numberInputs.forEach((input) => {
        input.value = '';
      });
    });

    // Reset main drawer checkboxes and inputs
    const mainCheckboxes = this.mobileForm.querySelectorAll('input[type="checkbox"]:not([data-nested-drawer])');
    mainCheckboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    const mainNumberInputs = this.mobileForm.querySelectorAll('input[type="number"]:not([data-nested-drawer])');
    mainNumberInputs.forEach((input) => {
      input.value = '';
    });

    // Reset sort selection if exists
    if (this.mobileSortBy) {
      const defaultOption = this.mobileSortBy.querySelector('option[selected]');
      if (defaultOption) {
        this.mobileSortBy.value = defaultOption.value;
      }
    }

    // Hide footer buttons
    this.footerButtons?.classList.add('is-hidden');

    // Reset desktop filters and sync with mobile
    const desktopForm = document.querySelector('#CollectionFiltersForm');
    if (desktopForm) {
      desktopForm.reset();
      const desktopCheckboxes = desktopForm.querySelectorAll('input[type="checkbox"]');
      desktopCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
        // Find and uncheck corresponding mobile input
        const mobileInput = this.mobileForm.querySelector(`input[name="${checkbox.name}"][value="${checkbox.value}"]`);
        if (mobileInput) {
          mobileInput.checked = false;
        }
      });

      const desktopNumberInputs = desktopForm.querySelectorAll('input[type="number"]');
      desktopNumberInputs.forEach((input) => {
        input.value = '';
        // Find and clear corresponding mobile input
        const mobileInput = this.mobileForm.querySelector(`input[name="${input.name}"]`);
        if (mobileInput) {
          mobileInput.value = '';
        }
      });
    }

    // Apply the reset and update both mobile and desktop
    this.filters.renderPage('');
    this.closeDrawer();
  }

  updateMobileDrawerContent(html) {
    const mobileDrawer = document.querySelector('sib-mobile-drawer');
    if (!mobileDrawer) return;

    // Update active filters
    const newActiveFilters = html.querySelector('.sib-mobile-filter-drawer__active-filters');
    const currentActiveFilters = document.querySelector('.sib-mobile-filter-drawer__active-filters');
    if (newActiveFilters && currentActiveFilters) {
      currentActiveFilters.innerHTML = newActiveFilters.innerHTML;

      // Add "Remove All" button if there are active filters
      const activeFilterButtons = currentActiveFilters.querySelectorAll('.sib-mobile-filter-drawer__remove-button');
      if (
        activeFilterButtons.length > 0 &&
        !currentActiveFilters.querySelector('.sib-mobile-filter-drawer__remove-all')
      ) {
        const removeAllButton = document.createElement('a');
        removeAllButton.href = window.location.pathname;
        removeAllButton.classList.add(
          'sib-mobile-filter-drawer__remove-button',
          'sib-mobile-filter-drawer__remove-all'
        );
        removeAllButton.textContent = 'Remove All';
        currentActiveFilters.insertBefore(removeAllButton, currentActiveFilters.firstChild);
      }
    }

    // Update footer buttons visibility
    mobileDrawer.updateFooterButtonsVisibility();
  }

  updateMobileProductCount(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newCount = doc.querySelector('.sib-collection-filters__mobile-count');
    const currentCount = document.querySelector('.sib-collection-filters__mobile-count');

    if (newCount && currentCount) {
      currentCount.innerHTML = newCount.innerHTML;
    }
  }
}

customElements.define('sib-mobile-drawer', SibMobileFilterDrawer);

class SibCollectionFilters extends HTMLElement {
  constructor() {
    super();
    if (window.sibCollectionFiltersInstance) {
      return window.sibCollectionFiltersInstance;
    }

    this.filterForm = document.querySelector('#CollectionFiltersForm');
    this.productGrid = document.querySelector('#product-grid');
    this.productCount = document.querySelector('.sib-collection-show-results-bar__count');
    this.sortBy = document.querySelector('#SortBy');
    this.onFilterChange = this.onFilterChange.bind(this);

    window.sibCollectionFiltersInstance = this;
    this.bindEvents();
    this.initializeDesktopShowMore();
    this.applyDefaultFilterForCollection();
  }

  bindEvents() {
    if (this.filterForm) {
      // Remove any existing listeners first
      this.filterForm.removeEventListener('change', this.onFilterChange);
      this.filterForm.addEventListener('change', this.onFilterChange);
    }

    if (this.sortBy) {
      const boundSortChange = this.onSortChange.bind(this);
      this.sortBy.removeEventListener('change', boundSortChange);
      this.sortBy.addEventListener('change', boundSortChange);
    }

    // Single event listener for pagination and remove buttons
    document.removeEventListener('click', this.handleClick);
    document.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick = (event) => {
    // Handle pagination
    const paginationLink = event.target.closest('.sib-collection-pagination__item');
    if (paginationLink && !paginationLink.classList.contains('sib-collection-pagination__item--current')) {
      event.preventDefault();
      const url = new URL(paginationLink.href);
      this.renderPage(url.searchParams.toString(), true);
      return;
    }

    // Handle remove buttons
    const removeButton = event.target.closest('.sib-collection-filters__remove-button');
    if (removeButton) {
      event.preventDefault();
      event.stopPropagation();

      if (
        removeButton.classList.contains('sib-collection-filters__remove-all') ||
        removeButton.getAttribute('aria-label') === 'Clear all filters'
      ) {
        this.resetAllForms();
        this.renderPage('');
      } else {
        const url = new URL(removeButton.href);
        this.renderPage(url.searchParams.toString());
      }
    }
  };

  handleRemoveFilter(event) {
    const removeButton = event.target.closest('.sib-collection-filters__remove-button');
    if (!removeButton) return;

    event.preventDefault();
    event.stopPropagation();

    const url = new URL(removeButton.href);

    if (
      removeButton.classList.contains('sib-collection-filters__remove-all') ||
      removeButton.getAttribute('aria-label') === 'Clear all filters'
    ) {
      this.resetAllForms();
      this.renderPage('');
    } else {
      this.renderPage(url.searchParams.toString());
    }
  }

  resetAllForms() {
    // Reset desktop form
    if (this.filterForm) {
      this.filterForm.reset();
      const inputs = this.filterForm.querySelectorAll('input');
      inputs.forEach((input) => {
        if (input.type === 'checkbox') {
          input.checked = false;
        } else if (input.type === 'number') {
          input.value = '';
        }
      });
    }

    // Reset mobile form
    const mobileForm = document.querySelector('#CollectionFiltersMobileForm');
    if (mobileForm) {
      mobileForm.reset();
      const mobileInputs = mobileForm.querySelectorAll('input');
      mobileInputs.forEach((input) => {
        if (input.type === 'checkbox') {
          input.checked = false;
        } else if (input.type === 'number') {
          input.value = '';
        }
      });
    }
  }

  onSortChange(event) {
    event.preventDefault();
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('sort_by', event.target.value);
    this.renderPage(searchParams.toString());
  }

  onFilterChange(event) {
    event.preventDefault();
    event.stopPropagation();

    // Prevent duplicate events
    if (event.target._handlingChange) return;
    event.target._handlingChange = true;

    // Get form data from the desktop filter form
    const formData = new FormData(this.filterForm);
    const searchParams = new URLSearchParams(formData);

    // Preserve the current sort_by parameter if it exists
    const currentSort = new URLSearchParams(window.location.search).get('sort_by');
    if (currentSort) {
      searchParams.set('sort_by', currentSort);
    }

    // Reset to page 1 when filtering
    searchParams.delete('page');

    // Sync with mobile form without triggering change event
    const mobileForm = document.querySelector('#CollectionFiltersMobileForm');
    if (mobileForm) {
      const changedInput = event.target;
      const mobileInput = mobileForm.querySelector(`input[name="${changedInput.name}"][value="${changedInput.value}"]`);
      if (mobileInput) {
        if (changedInput.type === 'checkbox') {
          mobileInput.checked = changedInput.checked;
        } else if (changedInput.type === 'number') {
          mobileInput.value = changedInput.value;
        }
      }

      // Update mobile drawer state without triggering events
      const mobileDrawer = document.querySelector('sib-mobile-drawer');
      if (mobileDrawer) {
        mobileDrawer.updateFooterButtonsVisibility();
      }
    }

    // Debounce the renderPage call
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
    this.filterTimeout = setTimeout(() => {
      this.renderPage(searchParams.toString());
      // Reset the handling flag after the debounce
      event.target._handlingChange = false;
    }, 300);
  }

  async renderPage(searchParams, scrollToTop = false) {
    const url = `${window.location.pathname}?${searchParams}`;
    this.showLoading();

    // Push URL immediately so address bar reflects current filters
    this.updateURLHash(searchParams);

    try {
      await new Promise((resolve) => setTimeout(resolve, 100));

      const response = await fetch(url);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      this.renderProductGrid(html);
      this.renderFilterInputs(html);
      this.updateDesktopActiveFilters(html);
      this.updateProductCount(html);
      this.updateMobileDrawerContent(html);

      if (scrollToTop) {
        const scrollTarget = this.productGrid.offsetTop - 100;
        requestAnimationFrame(() => {
          window.scrollTo({
            top: scrollTarget,
            behavior: 'smooth',
          });
        });
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      this.hideLoading();
    }
  }

  updateDesktopActiveFilters(html) {
    try {
      const newActive = html.querySelector('.sib-collection-filters__active');
      if (!newActive) return;

      let currentActive = document.querySelector('.sib-collection-filters__active');
      if (currentActive) {
        currentActive.innerHTML = newActive.innerHTML;
        currentActive.className = newActive.className;
      } else {
        // If the active container didn't exist previously, append it after the form
        const form = document.querySelector('#CollectionFiltersForm');
        if (form) {
          form.appendChild(newActive.cloneNode(true));
        }
      }
    } catch (_) {
      // no-op
    }
  }

  updateMobileDrawerContent(html) {
    const mobileDrawer = document.querySelector('sib-mobile-drawer');
    if (!mobileDrawer) return;

    // Update active filters
    const newActiveFilters = html.querySelector('.sib-mobile-filter-drawer__active-filters');
    const currentActiveFilters = document.querySelector('.sib-mobile-filter-drawer__active-filters');
    if (newActiveFilters && currentActiveFilters) {
      currentActiveFilters.innerHTML = newActiveFilters.innerHTML;

      // Add "Remove All" button if there are active filters
      const activeFilterButtons = currentActiveFilters.querySelectorAll('.sib-mobile-filter-drawer__remove-button');
      if (
        activeFilterButtons.length > 0 &&
        !currentActiveFilters.querySelector('.sib-mobile-filter-drawer__remove-all')
      ) {
        const removeAllButton = document.createElement('a');
        removeAllButton.href = window.location.pathname;
        removeAllButton.classList.add(
          'sib-mobile-filter-drawer__remove-button',
          'sib-mobile-filter-drawer__remove-all'
        );
        removeAllButton.textContent = 'Remove All';
        currentActiveFilters.insertBefore(removeAllButton, currentActiveFilters.firstChild);
      }
    }

    // Update footer buttons visibility
    mobileDrawer.updateFooterButtonsVisibility();
  }

  showLoading() {
    // Add loading state immediately
    this.productGrid?.classList.add('sib-main-collection-product-grid--loading');
    const overlay = this.productGrid?.querySelector('.sib-loading-overlay');
    if (overlay) {
      overlay.classList.remove('sib-loading-overlay--hidden');
      // Force a reflow to ensure the transition works
      overlay.offsetHeight;
      overlay.style.opacity = '1';
    }
    // Disable all interactive elements during loading
    const interactiveElements = this.productGrid?.querySelectorAll('a, button');
    interactiveElements?.forEach((element) => {
      element.style.pointerEvents = 'none';
    });
  }

  hideLoading() {
    const overlay = this.productGrid?.querySelector('.sib-loading-overlay');
    if (overlay) {
      overlay.style.opacity = '0';
      // Wait for the transition to complete before hiding
      setTimeout(() => {
        overlay.classList.add('sib-loading-overlay--hidden');
        this.productGrid?.classList.remove('sib-main-collection-product-grid--loading');
        // Re-enable interactive elements
        const interactiveElements = this.productGrid?.querySelectorAll('a, button');
        interactiveElements?.forEach((element) => {
          element.style.pointerEvents = '';
        });
      }, 300); // Match this with CSS transition duration
    }
  }

  updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  renderProductGrid(html) {
    const newProductGrid = html.getElementById('product-grid');
    if (newProductGrid && this.productGrid) {
      this.productGrid.innerHTML = newProductGrid.innerHTML;
    }
  }

  renderFilterInputs(html) {
    const newFilterForm = html.getElementById('CollectionFiltersForm');
    if (newFilterForm && this.filterForm) {
      // Store current input states before updating
      const currentInputs = Array.from(this.filterForm.querySelectorAll('input')).map((input) => ({
        name: input.name,
        value: input.value,
        checked: input.checked,
        type: input.type,
      }));

      this.filterForm.innerHTML = newFilterForm.innerHTML;

      // Re-bind events for desktop filter form
      this.filterForm.removeEventListener('change', this.onFilterChange);
      this.filterForm.addEventListener('change', this.onFilterChange);

      // Restore input states
      currentInputs.forEach((inputState) => {
        const input = this.filterForm.querySelector(`input[name="${inputState.name}"][value="${inputState.value}"]`);
        if (input) {
          if (input.type === 'checkbox') {
            input.checked = inputState.checked;
          } else if (input.type === 'number') {
            input.value = inputState.value;
          }
        }
      });

      this.initializeDesktopShowMore(this.filterForm);
      this.applyDefaultFilterForCollection();
    }

    // Update mobile filter counts and states
    const mobileDrawer = document.querySelector('sib-mobile-drawer');
    if (mobileDrawer) {
      const newDrawerGroups = html.querySelectorAll('.sib-mobile-filter-drawer__group');
      const currentDrawerGroups = document.querySelectorAll('.sib-mobile-filter-drawer__group');

      currentDrawerGroups.forEach((group, index) => {
        const newGroup = newDrawerGroups[index];
        if (!newGroup) return;

        // Update value count
        const newValueCount = newGroup.querySelector('.sib-mobile-filter-drawer__value-count');
        const currentValueCount = group.querySelector('.sib-mobile-filter-drawer__value-count');
        if (newValueCount && currentValueCount) {
          currentValueCount.textContent = newValueCount.textContent;
        }

        // Update individual filter counts
        const newItems = newGroup.querySelectorAll('.sib-mobile-filter-drawer__count');
        const currentItems = group.querySelectorAll('.sib-mobile-filter-drawer__count');
        currentItems.forEach((item, itemIndex) => {
          const newItem = newItems[itemIndex];
          if (newItem) {
            item.textContent = newItem.textContent;
          }
        });
      });

      // Update footer buttons visibility
      mobileDrawer.updateFooterButtonsVisibility();
    }
  }

  // Attempt to auto-check a filter that matches the current collection
  applyDefaultFilterForCollection() {
    try {
      // Prevent re-applying
      if (window.sibDefaultFilterApplied) return;

      // Require filter form
      if (!this.filterForm) return;

      // Only apply when there are no active filter params in the URL
      const urlParams = new URLSearchParams(window.location.search);
      const hasFilterParams = Array.from(urlParams.keys()).some((key) =>
        key.startsWith('filter.') || key === 'q'
      );
      if (hasFilterParams) return;

      const collectionTitle = (this.filterForm.dataset.collectionTitle || '').trim();
      const collectionHandle = (this.filterForm.dataset.collectionHandle || '').trim();
      if (!collectionTitle && !collectionHandle) return;

      const normalize = (str) =>
        (str || '')
          .toString()
          .toLowerCase()
          .replace(/&/g, 'and')
          .replace(/\+/g, 'and')
          .replace(/\s+/g, ' ')
          .replace(/[_-]+/g, ' ')
          .replace(/[^a-z0-9]+/g, ' ')
          .trim();

      const titleNorm = normalize(collectionTitle);
      const handleNorm = normalize(collectionHandle);

      // Find a matching checkbox by label text or value
      const checkboxes = this.filterForm.querySelectorAll('input[type="checkbox"]');
      let targetInput = null;
      for (const input of checkboxes) {
        if (input.disabled) continue;
        const valueNorm = normalize(input.value);
        const labelText = input.closest('label')?.textContent || '';
        const labelNorm = normalize(labelText);

        if (
          (titleNorm && (valueNorm === titleNorm || labelNorm === titleNorm)) ||
          (handleNorm && (valueNorm === handleNorm || labelNorm === handleNorm))
        ) {
          targetInput = input;
          break;
        }
      }

      if (!targetInput) return;

      // Check desktop input
      if (!targetInput.checked) {
        targetInput.checked = true;
      }

      // Sync mobile counterpart silently
      const mobileForm = document.querySelector('#CollectionFiltersMobileForm');
      if (mobileForm) {
        const mobileInput = mobileForm.querySelector(
          `input[name="${targetInput.name}"][value="${CSS.escape(targetInput.value)}"]`
        );
        if (mobileInput && !mobileInput.disabled) {
          mobileInput.checked = true;
        }
      }

      // Build search params from the now-checked desktop form and preserve sort
      const formData = new FormData(this.filterForm);
      const searchParams = new URLSearchParams(formData);

      // Remove empty values (especially blank price range fields)
      Array.from(searchParams.keys()).forEach((key) => {
        const values = searchParams.getAll(key);
        const nonEmpty = values.filter((v) => v !== '' && v != null);
        if (nonEmpty.length === 0) {
          searchParams.delete(key);
        } else if (nonEmpty.length !== values.length) {
          searchParams.delete(key);
          nonEmpty.forEach((v) => searchParams.append(key, v));
        }
      });
      const currentSort = new URLSearchParams(window.location.search).get('sort_by');
      if (currentSort) {
        searchParams.set('sort_by', currentSort);
      }

      window.sibDefaultFilterApplied = true;
      // Apply via AJAX
      this.renderPage(searchParams.toString());
    } catch (e) {
      // Fail silently to avoid breaking filters
      // console.warn('applyDefaultFilterForCollection failed', e);
    }
  }

  initializeDesktopShowMore(rootElement) {
    const scope = rootElement || this.filterForm;
    if (!scope) return;

    const buttons = scope.querySelectorAll('[data-filter-show-more]');
    buttons.forEach((button) => {
      if (button.dataset.showMoreBound === 'true') return;
      button.dataset.showMoreBound = 'true';
      button.addEventListener('click', this.onDesktopShowMoreClick);
    });
  }

  onDesktopShowMoreClick = (event) => {
    const button = event.currentTarget;
    const filterBody = button.closest('.sib-collection-filters__body');
    const list = filterBody?.querySelector('[data-filter-list]');
    if (!list) return;

    const items = list.querySelectorAll('[data-filter-item]');
    if (!items.length) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    const nextExpandedState = !isExpanded;

    items.forEach((item) => {
      if (nextExpandedState) {
        item.classList.remove('is-hidden');
      } else {
        item.classList.add('is-hidden');
      }
    });

    button.setAttribute('aria-expanded', nextExpandedState.toString());

    const moreLabel = button.querySelector('[data-label-more]');
    const lessLabel = button.querySelector('[data-label-less]');
    moreLabel?.classList.toggle('is-hidden');
    lessLabel?.classList.toggle('is-hidden');
  };

  updateProductCount(html) {
    // Update desktop product count
    const newProductCount = html.querySelector('.sib-collection-show-results-bar__count');
    if (newProductCount && this.productCount) {
      this.productCount.innerHTML = newProductCount.innerHTML;
    }

    // Update mobile product count
    const newMobileCount = html.querySelector('.sib-collection-filters__mobile-count');
    const currentMobileCount = document.querySelector('.sib-collection-filters__mobile-count');
    if (newMobileCount && currentMobileCount) {
      currentMobileCount.innerHTML = newMobileCount.innerHTML;
    }
  }
}

customElements.define('sib-collection-filters', SibCollectionFilters);

if (!window.sibCollectionFiltersInstance) {
  window.addEventListener('load', () => {
    new SibCollectionFilters();
  });
}
